import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, Info, Printer, RefreshCw, Truck } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import {
  taService,
  TaConfigurationOverviewData,
  TaOverviewCarrier,
  TaOverviewConsignor,
  TaOverviewIssue,
  TaDeliveryMethodStatus
} from '../../../../../services/taService';

/**
 * Fraktoppsett hos TA (TW 36436874): avsendere × produkter per transportør, kontrollert mot
 * leveringsmåter og avsendermapping. Felles for alle TA-er — innholdet kommer fra providerens
 * ITaConfigurationProvider via GET /api/v1/ta/configuration.
 */
export const TaConfigurationOverview: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<TaConfigurationOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await taService.getConfiguration());
    } catch (e: any) {
      setError(e?.message ?? 'Ukjent feil');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const k = 'manager.modules.taConfiguration';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6" />
            {t(`${k}.title`, {}, 'Fraktoppsett hos TA')}
            {data?.softwareName && <span className="text-gray-500 font-normal">— {data.softwareName}</span>}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t(`${k}.subtitle`, {}, 'Avsendere, transportører og produkter hos fraktleverandøren, kontrollert mot leveringsmåter og avsendermapping')}
          </p>
          {data?.status === 'Ok' && (
            <p className="text-xs text-gray-500 mt-1">
              {data.accountReference && <>{t(`${k}.account`, { '0': data.accountReference }, 'Oppsett {0}')} · </>}
              {t(`${k}.fetchedAt`, { '0': new Date(data.fetchedAt).toLocaleString('nb-NO') }, 'Hentet {0}')}
            </p>
          )}
        </div>
        <div className="flex gap-2 print:hidden">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t(`${k}.refresh`, {}, 'Oppdater')}
          </button>
          <button
            onClick={() => window.print()}
            disabled={!data || data.status !== 'Ok'}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
            {t(`${k}.print`, {}, 'Skriv ut')}
          </button>
        </div>
      </div>

      {loading && !data && <p className="text-gray-500">{t(`${k}.loading`, {}, 'Henter oppsett fra TA...')}</p>}

      {error && (
        <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-red-700">{error}</div>
      )}

      {data && data.status !== 'Ok' && (
        <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800">
          <div className="font-medium">{t(`${k}.status.${data.status}`, {}, data.status)}</div>
          {data.message && <div className="text-sm mt-1">{data.message}</div>}
        </div>
      )}

      {data && data.status === 'Ok' && (
        <>
          <SummaryTiles data={data} k={k} />
          <IssueList issues={data.issues} k={k} />
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t(`${k}.sections.matrix`, {}, 'Produkter per avsender')}</h2>
            <p className="text-xs text-gray-500">
              {t(`${k}.matrix.legend`, {}, '✓ = produktet er tilgjengelig for avsenderen. + = tilleggstjenester (hold over for detaljer).')}
              {data.summary.hasMappingCodes && (
                <> {t(`${k}.matrix.mappingNote`, {}, 'Mappingkoder er TA-ens egne og brukes ikke av Compass ved booking.')}</>
              )}
            </p>
            {data.carriers.map(carrier => (
              <CarrierMatrix key={carrier.id} carrier={carrier} data={data} k={k} />
            ))}
          </section>
          <DeliveryMethodTable data={data} k={k} />
        </>
      )}
    </div>
  );
};

const SummaryTiles: React.FC<{ data: TaConfigurationOverviewData; k: string }> = ({ data, k }) => {
  const { t } = useTranslation();
  const s = data.summary;
  const tiles: { label: string; value: string; alert?: boolean }[] = [
    { label: t(`${k}.stats.consignors`, {}, 'Avsendere'), value: String(s.consignorCount) },
    { label: t(`${k}.stats.carriers`, {}, 'Transportører'), value: String(s.carrierCount) },
    { label: t(`${k}.stats.products`, {}, 'Ulike produkter'), value: String(s.distinctProductCount) },
    ...(s.hasMappingCodes
      ? [{ label: t(`${k}.stats.mappingCodes`, {}, 'Mappingkoder satt'), value: `${s.mappingCodesSet} / ${s.mappingCodesTotal}` }]
      : []),
    { label: t(`${k}.stats.deliveryMethodsToTa`, {}, 'Leveringsmåter til TA'), value: String(s.deliveryMethodsToTa) },
    {
      label: t(`${k}.stats.deliveryMethodsWithErrors`, {}, 'Leveringsmåter med feil'),
      value: String(s.deliveryMethodsWithErrors),
      alert: s.deliveryMethodsWithErrors > 0
    }
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {tiles.map(tile => (
        <div
          key={tile.label}
          className={`p-3 rounded-lg border ${tile.alert ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}
        >
          <div className={`text-2xl font-semibold ${tile.alert ? 'text-red-700' : 'text-gray-900'}`}>{tile.value}</div>
          <div className="text-xs text-gray-500">{tile.label}</div>
        </div>
      ))}
    </div>
  );
};

const IssueList: React.FC<{ issues: TaOverviewIssue[]; k: string }> = ({ issues, k }) => {
  const { t } = useTranslation();
  const style = {
    error: { cls: 'border-red-200 bg-red-50 text-red-700', Icon: AlertCircle },
    warning: { cls: 'border-amber-200 bg-amber-50 text-amber-800', Icon: AlertTriangle },
    info: { cls: 'border-blue-200 bg-blue-50 text-blue-700', Icon: Info }
  } as const;
  const order = { error: 0, warning: 1, info: 2 } as const;
  const sorted = [...issues].sort((a, b) => order[a.severity] - order[b.severity]);

  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold text-gray-900">{t(`${k}.sections.issues`, {}, 'Avvik')}</h2>
      {sorted.length === 0 && (
        <div className="p-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700">
          {t(`${k}.sections.noIssues`, {}, 'Ingen avvik mellom TA-oppsettet og Compass.')}
        </div>
      )}
      {sorted.map((issue, i) => {
        const { cls, Icon } = style[issue.severity] ?? style.info;
        return (
          <div key={i} className={`flex items-start gap-2 p-2 rounded-lg border text-sm ${cls}`}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{issue.message}</span>
          </div>
        );
      })}
    </section>
  );
};

const CarrierMatrix: React.FC<{ carrier: TaOverviewCarrier; data: TaConfigurationOverviewData; k: string }> = ({ carrier, data, k }) => {
  const { t } = useTranslation();
  const columns: TaOverviewConsignor[] = data.consignors.filter(c => c.id in carrier.mappingCodeByConsignor);
  const hasMappingCodes = data.summary.hasMappingCodes;

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto break-inside-avoid">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 font-medium text-gray-900">
        {carrier.name} <span className="text-gray-500 font-normal">({carrier.id})</span>
      </div>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left px-3 py-2 text-gray-700 align-bottom">{t(`${k}.matrix.product`, {}, 'Produkt')}</th>
            {columns.map(c => (
              <th key={c.id} className="px-2 py-2 text-center text-gray-700 align-bottom min-w-[6rem]">
                <div className="font-medium leading-tight">{c.name}</div>
                <div className="text-xs text-gray-500 font-normal">{c.id}</div>
                {c.isCompassDefault && (
                  <span className="inline-block mt-1 px-1.5 rounded text-xs font-normal bg-blue-100 text-blue-800">
                    {t(`${k}.matrix.compassDefault`, {}, 'Standard')}
                  </span>
                )}
                {data.usesConsignorMapping && c.compassConsignorCodes.length === 0 && (
                  <span className="inline-block mt-1 px-1.5 rounded text-xs font-normal bg-amber-100 text-amber-700">
                    {t(`${k}.matrix.notInCompass`, {}, 'Ikke koblet i Compass')}
                  </span>
                )}
              </th>
            ))}
            <th className="text-left px-3 py-2 text-gray-700 align-bottom">{t(`${k}.matrix.deliveryMethods`, {}, 'Leveringsmåter')}</th>
          </tr>
        </thead>
        <tbody>
          {carrier.products.map(p => (
            <tr key={p.code} className="border-b border-gray-200">
              <td className="px-3 py-1.5 text-gray-900 whitespace-nowrap">
                <span className="font-mono text-gray-500 mr-2">{p.code}</span>{p.name}
                {p.alternateCode && <span className="font-mono text-xs text-gray-500 ml-2">({p.alternateCode})</span>}
              </td>
              {columns.map(c => {
                const cell = p.cells[c.id];
                if (!cell) return <td key={c.id} className="px-2 py-1.5 text-center text-gray-500">–</td>;
                const services = cell.additionalServices;
                const title = services.map(s => `${s.code} ${s.name}${s.mappingCode ? ` [${s.mappingCode}]` : ''}`).join('\n');
                return (
                  <td key={c.id} className="px-2 py-1.5 text-center bg-green-50 text-green-700" title={title || undefined}>
                    ✓{services.length > 0 && <sup className="ml-0.5">+{services.length}</sup>}
                    {cell.mappingCode && <div className="text-xs font-mono">{cell.mappingCode}</div>}
                  </td>
                );
              })}
              <td className="px-3 py-1.5 text-gray-700">{p.deliveryMethodNos.join(', ')}</td>
            </tr>
          ))}
          {hasMappingCodes && (
            <tr className="bg-gray-50">
              <td className="px-3 py-1.5 text-xs text-gray-500">{t(`${k}.matrix.carrierMappingCode`, {}, 'Mappingkode transportør')}</td>
              {columns.map(c => (
                <td key={c.id} className="px-2 py-1.5 text-center text-xs font-mono text-gray-700">
                  {carrier.mappingCodeByConsignor[c.id] ?? '–'}
                </td>
              ))}
              <td />
            </tr>
          )}
          <tr className="bg-gray-50">
            <td className="px-3 py-1.5 text-xs text-gray-500">{t(`${k}.matrix.productsPerConsignor`, {}, 'Produkter')}</td>
            {columns.map(c => (
              <td key={c.id} className="px-2 py-1.5 text-center text-xs text-gray-700">
                {carrier.products.filter(p => c.id in p.cells).length}
              </td>
            ))}
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const DeliveryMethodTable: React.FC<{ data: TaConfigurationOverviewData; k: string }> = ({ data, k }) => {
  const { t } = useTranslation();
  const statusClass: Record<TaDeliveryMethodStatus, string> = {
    Ok: 'bg-green-100 text-green-800',
    NotToTa: 'bg-gray-100 text-gray-700',
    CarrierNotFound: 'bg-red-50 text-red-700',
    ProductNotFound: 'bg-red-50 text-red-700',
    ProductMissing: 'bg-red-50 text-red-700'
  };
  const consignorName = (id: string) => data.consignors.find(c => c.id === id)?.name ?? id;

  return (
    <section className="space-y-2 break-inside-avoid">
      <h2 className="text-lg font-semibold text-gray-900">{t(`${k}.sections.deliveryMethods`, {}, 'Leveringsmåter')}</h2>
      <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200 text-left text-gray-700">
              <th className="px-3 py-2">{t(`${k}.dm.no`, {}, 'Nr')}</th>
              <th className="px-3 py-2">{t(`${k}.dm.description`, {}, 'Beskrivelse')}</th>
              <th className="px-3 py-2">{t(`${k}.dm.carrier`, {}, 'Transportør')}</th>
              <th className="px-3 py-2">{t(`${k}.dm.product`, {}, 'Produkt')}</th>
              <th className="px-3 py-2">{t(`${k}.dm.status`, {}, 'Status')}</th>
              <th className="px-3 py-2">{t(`${k}.dm.missingAt`, {}, 'Mangler hos avsender')}</th>
            </tr>
          </thead>
          <tbody>
            {data.deliveryMethods.map(dm => (
              <tr key={dm.deliveryMethodNo} className="border-b border-gray-200">
                <td className="px-3 py-1.5 text-gray-900">{dm.deliveryMethodNo}</td>
                <td className="px-3 py-1.5 text-gray-900">{dm.description}</td>
                <td className="px-3 py-1.5 text-gray-700">
                  {dm.carrierId ? <>{dm.carrierName ?? ''} <span className="text-gray-500">({dm.carrierId})</span></> : '–'}
                </td>
                <td className="px-3 py-1.5 text-gray-700">
                  {dm.productCode ? <><span className="font-mono">{dm.productCode}</span> {dm.productName ?? ''}</> : '–'}
                </td>
                <td className="px-3 py-1.5">
                  <span className={`px-2 py-0.5 rounded text-xs ${statusClass[dm.status]}`}>
                    {t(`${k}.dmStatus.${dm.status}`, {}, dm.status)}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-gray-700">
                  {dm.missingAtMappedConsignorIds.map(id => `${id} ${consignorName(id)}`).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
